import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SecurityPresenter from "./SecurityPresenter";
import {
    TwoFactorStatus,
    TwoFactorSetup,
    EnableResponse
} from "./models";

import { ContentBox } from "@arelia-components/ui-atoms/ContentBox";
import Header from "@arelia-components/layout/Header";
import LogoutForm from "@arelia-components/api-authorization/LogoutForm";
import { Button } from "@mui/material";
import { PATHS } from "src/routes";

async function api<T>(url: string, init?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        ...init
    });
    if (!res.ok) {
        let err: any;
        try {
            err = await res.json();
        } catch {
        }
        throw err || { error: res.statusText };
    }
    return (await res.json()) as T;
}

export default function SecurityContainer() {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const navOptions = [
        { value: PATHS.PROFILE, label: "Profile" },
        { value: PATHS.CONNECTIONS, label: "Connections" },
        { value: PATHS.SUBSCRIPTION, label: "Subscription" },
        { value: PATHS.SECURITY, label: "Security" }
    ];

    const action = (
        <LogoutForm>
            <Button type="submit" variant="contained" color="secondary">
                Logout
            </Button>
        </LogoutForm>
    );

    const [status, setStatus] = useState<TwoFactorStatus | null>(null);
    const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
    const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [actionMessage, setActionMessage] = useState<string | null>(null);

    const clearMessages = () => {
        setError(null);
        setActionMessage(null);
    };

    const loadStatus = useCallback(async () => {
        try {
            setStatus(await api<TwoFactorStatus>("/api/account/2fa/status"));
        } catch (e: any) {
            setError(e?.error || "Failed to load 2FA status");
        }
    }, []);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const beginSetup = async () => {
        clearMessages();
        try {
            setSetup(await api<TwoFactorSetup>("/api/account/2fa/setup"));
        } catch (e: any) {
            setError(e?.error || "Unable to start setup");
        }
    };

    const enable = async (code: string) => {
        setLoading(true);
        clearMessages();
        try {
            const resp = await api<EnableResponse>("/api/account/2fa/enable", {
                method: "POST",
                body: JSON.stringify({ code })
            });
            setRecoveryCodes(resp.recoveryCodes ?? null);
            setSetup(null);
            await loadStatus();
            setActionMessage("Two-factor authentication enabled.");
        } catch (e: any) {
            setError(e?.error || "Invalid verification code");
        } finally {
            setLoading(false);
        }
    };

    const generateRecoveryCodes = async () => {
        setLoading(true);
        clearMessages();
        try {
            const resp = await api<{ recoveryCodes: string[] }>(
                "/api/account/2fa/recovery-codes",
                { method: "POST", body: JSON.stringify({}) }
            );
            setRecoveryCodes(resp.recoveryCodes);
            await loadStatus();
            setActionMessage("New recovery codes generated.");
        } catch (e: any) {
            setError(e?.error || "Failed to generate recovery codes");
        } finally {
            setLoading(false);
        }
    };

    const disable = async () => {
        setLoading(true);
        clearMessages();
        try {
            await api<{ disabled: boolean }>("/api/account/2fa/disable", {
                method: "POST"
            });
            setRecoveryCodes(null);
            await loadStatus();
            setActionMessage("Two-factor authentication disabled.");
        } catch (e: any) {
            setError(e?.error || "Failed to disable 2FA");
        } finally {
            setLoading(false);
        }
    };

    const reset = async () => {
        setLoading(true);
        clearMessages();
        try {
            const resp = await api<TwoFactorSetup>("/api/account/2fa/reset", {
                method: "POST"
            });
            setSetup(resp);
            setRecoveryCodes(null);
            await loadStatus();
            setActionMessage("Authenticator key reset. Scan new QR and re-enable.");
        } catch (e: any) {
            setError(e?.error || "Failed to reset 2FA");
        } finally {
            setLoading(false);
        }
    };

    return (
        <ContentBox>
            <Header
                title="Account"
                action={action}
                navOptions={navOptions}
                navValue={pathname}
                setNavValue={navigate}
            />
            <SecurityPresenter
                status={status}
                setup={setup}
                recoveryCodes={recoveryCodes}
                loading={loading}
                error={error}
                actionMessage={actionMessage}
                beginSetup={beginSetup}
                enable={enable}
                generateRecoveryCodes={generateRecoveryCodes}
                disable={disable}
                reset={reset}
                clearMessages={clearMessages}
            />
        </ContentBox>
    );
}
